import React, { AnchorHTMLAttributes, ButtonHTMLAttributes, CSSProperties, FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { HTMLAnchorElement, HTMLButtonElement } from 'window-or-global';
import CircularLoader from '../circular-loader/circular-loader-component';
import Icon, { IconName } from '../icons/icon/icon-component';
import Tooltip from '../tooltip/tooltip-component';
import styles from './button-component.module.scss';

export type ButtonProps = {
  buttonType?: 'primary' | 'secondary' | 'tertiary' | 'text' | 'link' | 'link-white' | 'outline' | 'outline-white' | 'ghost' | string;
  text?: string;
  textOnlyAlignment?: 'start' | 'center' | 'end';
  /**
   * small-icon can only be used with an icon only button;
   * full-width can be used with any text button
   */
  size?: 'default' | 'full-width' | 'small-icon';
  isIconSmall?: boolean;
  disabled?: boolean;
  icon?: IconName | string | 'circular-loader';
  iconPosition?: 'left' | 'right';
  /**
   * if false, will use the react-router <Link> to redirect without refreshing
   */
  isExternalLink?: boolean;
  /**
   * custom tooltip class to style the title attribute
   */
  tooltipClass?: string;
} & (ButtonHTMLAttributes<HTMLButtonElement> | AnchorHTMLAttributes<HTMLAnchorElement>);

/**
 * Button Atom which can render either an html `button` tag or an `a` tag depending on the given props.
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=0%3A1
 */
export const Button: FunctionComponent<ButtonProps> = ({
  buttonType = 'primary',
  text = '',
  textOnlyAlignment,
  size = 'default',
  isIconSmall = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  isExternalLink = false,
  tooltipClass,
  className,
  title,
  children,
  ...anchorAttributes
}) => {
  //-- if button has icon only (no text) --//
  let iconButtonClass: string = '';
  if (icon && !text) iconButtonClass = styles['icon-button'];

  const componentClass = `${styles['button-component']} ${styles[buttonType === undefined ? 'text' : buttonType]} ${
    styles[size]
  } ${iconButtonClass} ${className ?? ''} ${iconPosition === 'right' ? styles['icon-right'] : ''}  ${disabled ? styles.disabled : ''}`;

  // Disables tab selection when the button is disabled
  const tabIndex = disabled ? -1 : 0;

  return title ? (
    <Tooltip title={title} className={tooltipClass}>
      {getOuterElement()}
    </Tooltip>
  ) : (
    getOuterElement()
  );

  function getOuterElement() {
    if ((anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>).href !== undefined) {
      if (isExternalLink) {
        const { onClick, ...aAttributes } = anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>;
        return (
          <a
            className={componentClass}
            tabIndex={tabIndex}
            onClick={disabled ? undefined : onClick}
            aria-disabled={disabled}
            aria-label={icon}
            {...aAttributes}
          >
            {getInnerFragment()}
          </a>
        );
      } else {
        const { href, onClick, ...aAttributes } = anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>;
        return (
          <Link
            className={componentClass}
            tabIndex={tabIndex}
            onClick={disabled ? undefined : onClick}
            aria-disabled={disabled}
            to={href ?? ''}
            aria-label={icon}
            {...aAttributes}
          >
            {getInnerFragment()}
          </Link>
        );
      }
    } else {
      const { onClick, ...aAttributes } = anchorAttributes as ButtonHTMLAttributes<HTMLButtonElement>;
      return (
        <>
          <button
            type='button'
            className={componentClass}
            tabIndex={tabIndex}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            aria-label={icon}
            {...aAttributes}
          >
            {getInnerFragment()}
          </button>
        </>
      );
    }
  }

  function getInnerFragment() {
    return (
      <>
        {icon ? (
          icon !== 'circular-loader' ? (
            <Icon name={icon} className={styles.icon} width={isIconSmall ? 16 : 24} height={24} />
          ) : (
            <CircularLoader className={`${styles.icon} ${styles.loader}`} width={size === 'small-icon' ? 16 : 24} />
          )
        ) : null}
        {/* span added because of: https://github.com/facebook/react/issues/11538#issuecomment-390386520 */}
        <span
          className={(children ?? text) && textOnlyAlignment && icon === undefined ? styles['text-only-alignment'] : ''}
          style={{ '--text-only-alignment': textOnlyAlignment ?? '' } as CSSProperties}
        >
          {children ?? text}
        </span>
      </>
    );
  }
};

export default Button;
